import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, CheckCircle2, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { resolveDuplicate } from "@/lib/apiClient";

export const DuplicateDecision = ({ inv, user, onResolved }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const dup = inv.possible_duplicate;
  const done = inv.duplicate_resolved;

  const decide = async (decision) => {
    setBusy(decision);
    try {
      const updated = await resolveDuplicate(inv.id, user, decision, null);
      onResolved?.(updated);
      decision === "keep" ? toast.success("Kept — no longer flagged as a duplicate") : toast.error("Rejected as a duplicate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the decision");
    } finally {
      setBusy(null);
    }
  };

  if (!dup || done) {
    if (!done) return null;
    return (
      <div data-testid="duplicate-resolved" className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
        {done.decision === "keep" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Ban className="h-4 w-4 text-red-500" />}
        <span>
          {done.decision === "keep" ? "Checked — not a duplicate" : "Confirmed duplicate — rejected"} of{" "}
          <button className="font-mono underline" onClick={() => navigate(`/invoice/${done.match?.invoice_id}`)} data-testid="duplicate-resolved-match">{done.match?.invoice_number || "matched invoice"}</button>
          {" "}by {done.by} · {new Date(done.at).toLocaleString()}
        </span>
      </div>
    );
  }

  const open = !["approved", "rejected"].includes(inv.status);
  return (
    <div data-testid="duplicate-warning" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm">
          <Copy className="h-5 w-5 text-amber-500 shrink-0" />
          <span>
            <span className="font-medium text-amber-500">Possible duplicate. </span>
            {dup.reason} ({dup.invoice_number}). Decide what to do with it:
          </span>
        </div>
        <Button data-testid="view-duplicate-btn" variant="outline" size="sm" className="border-amber-500/40 text-amber-500 hover:bg-amber-500/10 shrink-0" onClick={() => navigate(`/invoice/${dup.invoice_id}`)}>
          View match
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400" onClick={() => decide("keep")} disabled={!!busy} data-testid="duplicate-keep-btn">
          {busy === "keep" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Not a duplicate — keep
        </Button>
        {inv.status !== "rejected" && (
          <Button size="sm" variant="outline" className="h-8 border-red-500/40 text-red-600 hover:bg-red-500/10 dark:text-red-400" onClick={() => decide("reject")} disabled={!!busy} data-testid="duplicate-reject-btn">
            {busy === "reject" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Ban className="h-3.5 w-3.5 mr-1" />} It's a duplicate — reject
          </Button>
        )}
        <span className="text-[11px] text-muted-foreground">
          {open ? "Keeping clears the flag; rejecting closes it and it is never sent to your accounting system." : inv.status === "approved" ? "Already approved — keeping clears the flag and retries any failed sync; rejecting withdraws it." : "Already rejected."}
        </span>
      </div>
    </div>
  );
};
